<template>
    <div>
        <div class="table table-sm mb-0 hide-header">
            <Draggable v-model="localValue"
                       :disabled="editingRowIndex >= 0"
                       handle=".drag-handle"
                       @end="onDragEnd"
                       tag="div"
                       class="table-body">
                <div v-for="(item, index) in localValue" :key="index"
                     class="table-row d-flex align-items-center">
                    <!-- Handle column -->
                    <div class="table-cell drag-handle-column">
                        <div class="drag-handle" :class="{ 'text-muted': editingRowIndex >= 0 }" style="cursor: move;">
                            <i class="fas fa-grip-vertical"></i>
                        </div>
                    </div>

                    <!-- Code column -->
                    <div class="table-cell code-column">
                        <b-input v-if="editingRowIndex === index" v-model="editingRowData.code"
                            placeholder="Code" autofocus maxlength="50" size="sm" />
                        <strong v-else>/{{ item.code }}</strong>
                    </div>

                    <!-- Category column -->
                    <div class="table-cell category-column">
                        <b-input v-if="editingRowIndex === index" v-model="editingRowData.category"
                            placeholder="Category" maxlength="100" size="sm" />
                        <small v-else class="text-muted">{{ item.category }}</small>
                    </div>

                    <!-- Expansion column -->
                    <div class="table-cell flex-fill">
                        <b-form-textarea v-if="editingRowIndex === index" v-model="editingRowData.expansion"
                            placeholder="Expansion text" rows="1" size="sm" class="textarea-autosize" />
                        <span v-else class="text-truncate d-inline-block" style="max-width: 400px;">{{ item.expansion }}</span>
                    </div>

                    <!-- Actions column -->
                    <div class="table-cell actions">
                        <div class="d-flex" style="gap: 6px;">
                            <template v-if="editingRowIndex !== index">
                                <b-button variant="link" @click.prevent="startEdit(item, index)" title="Edit">
                                    <b-icon icon="pencil" variant="primary" />
                                </b-button>
                                <b-button variant="link" @click.prevent="removeItem(index)" title="Remove">
                                    <b-icon icon="x-circle-fill" variant="danger" />
                                </b-button>
                            </template>
                            <template v-else>
                                <b-button variant="link" title="Save" @click.prevent="saveEdit(index)"
                                    :disabled="!editingRowData.code || !editingRowData.expansion">
                                    <b-icon icon="save" variant="primary" />
                                </b-button>
                                <b-button variant="link" title="Cancel Edit" @click.prevent="cancelEdit">
                                    <b-icon icon="x-lg" variant="" />
                                </b-button>
                            </template>
                        </div>
                    </div>
                </div>
            </Draggable>
        </div>

        <b-button size="sm" variant="primary" @click="addItem" class="mt-1">
            Add Shortcode
        </b-button>
    </div>
</template>

<script lang="ts">
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import Draggable from 'vuedraggable';
import { TextShortcode } from "@/models/TextShortcode";

@Component({
    name: "TextShortcodesField",
    components: {
        Draggable
    }
})
export default class TextShortcodesField extends Vue {
    @Prop({ default: () => [] }) value!: TextShortcode[];

    localValue: TextShortcode[] = [];
    editingRowData: TextShortcode = { code: '', expansion: '', sortOrder: 0 };
    editingRowIndex: number = -1;

    created() {
        this.localValue = [...(this.value || [])];
    }

    @Watch('value')
    onValueChanged(newVal: TextShortcode[]) {
        if (this.editingRowIndex < 0) {
            this.localValue = [...(newVal || [])];
        }
    }

    emitUpdate() {
        this.$emit('input', [...this.localValue]);
    }

    onDragEnd() {
        for (let i = 0; i < this.localValue.length; i++) {
            this.localValue[i].sortOrder = i;
        }
        this.emitUpdate();
    }

    startEdit(item: TextShortcode, index: number) {
        this.editingRowIndex = index;
        this.editingRowData = { ...item };
    }

    async addItem() {
        if (this.editingRowIndex >= 0) {
            this.saveEdit(this.editingRowIndex);
            await this.$nextTick();
        }

        const newItem: TextShortcode = { code: '', expansion: '', sortOrder: this.localValue.length };
        this.localValue.push(newItem);
        this.startEdit(newItem, this.localValue.length - 1);
    }

    removeItem(index: number) {
        this.localValue.splice(index, 1);
        for (let i = 0; i < this.localValue.length; i++) {
            this.localValue[i].sortOrder = i;
        }
        this.emitUpdate();
    }

    cancelEdit() {
        const index = this.editingRowIndex;
        const item = this.localValue[index];
        // If new unsaved item, remove it
        if (!item.code) {
            this.localValue.splice(index, 1);
        }
        this.editingRowIndex = -1;
    }

    saveEdit(index: number) {
        if (!this.editingRowData.code?.trim() || !this.editingRowData.expansion?.trim()) {
            if (!this.editingRowData.code?.trim() && !this.editingRowData.expansion?.trim()) {
                this.localValue.splice(index, 1);
                this.editingRowIndex = -1;
                return;
            }
            return;
        }

        const data = { ...this.editingRowData };
        data.code = data.code.replace(/^\/+/, '');
        this.localValue.splice(index, 1, data);
        this.editingRowIndex = -1;
        this.emitUpdate();
    }
}
</script>

<style scoped lang="scss">
::v-deep(thead th),
::v-deep(td) {
    padding: 1px 7px !important;
}

::v-deep(td) {
    height: 38px !important;
}

table ::v-deep(.actions) {
    width: 90px;
}

table ::v-deep(.drag-handle-column) {
    width: 30px;
    text-align: center;
}

.drag-handle {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;

    &.text-muted {
        cursor: not-allowed !important;
        opacity: 0.5;
    }
}

.table {
    &.hide-header .table-body .table-row:first-child {
    }
}

.table-body {
    display: block;
}

.table-row {
    border: 1px solid #dee2e6;
    border-top: 0;
    padding: 1px 7px;
    min-height: 38px;

    &:first-child {
        border-top: 1px solid #dee2e6;
    }

    &:hover {
        background-color: rgba(0, 0, 0, 0.075);
    }
}

.table-cell {
    display: flex;
    align-items: center;

    &.drag-handle-column {
        width: 30px;
        flex-shrink: 0;
        text-align: center;
        padding: 0;
    }

    &.code-column {
        width: 140px;
        flex-shrink: 0;
        padding-right: 0.5rem;
    }

    &.category-column {
        width: 120px;
        flex-shrink: 0;
        padding-right: 0.5rem;
    }

    &.actions {
        width: 90px;
        flex-shrink: 0;
        justify-content: flex-end;
        padding-left: 0.5rem;
    }

    &.flex-fill {
        flex: 1;
        padding-right: 0.5rem;
    }
}
</style>
